
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotificationDispatcher } from '@/hooks/useNotificationDispatcher';
import { fetchRecipientProfile } from '@/utils/profiles/fetchRecipientProfile';

export const useDocumentEmailNotifier = () => {
  const { dispatch } = useNotificationDispatcher();
  
  // Helper function to get user's email from user ID
  const getUserEmail = async (userId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', userId)
        .single();
        
      if (error) throw error;
      return data?.email || null;
    } catch (error) {
      console.error('Error fetching user email:', error);
      return null;
    }
  };
  
  const notifyDocumentVerified = useCallback(async (userId: string, recipientName?: string) => {
    try {
      console.log(`Starting document verified notification for user: ${userId}`);
      
      // Get the user's email from their ID
      const recipientEmail = await getUserEmail(userId);
      if (!recipientEmail) {
        console.error('Could not find email for user:', userId);
        return false;
      }
      
      console.log(`Found email for user ${userId}: ${recipientEmail}`);
      
      // If recipientName is not provided, fetch it
      if (!recipientName) {
        const { recipientName: fetchedName } = await fetchRecipientProfile(recipientEmail);
        recipientName = fetchedName;
      }
      
      console.log(`Dispatching documentVerified notification to ${recipientEmail}`);
      
      return await dispatch('documentVerified', recipientEmail, {
        recipientName
      });
    } catch (error) {
      console.error('Error sending document verified notification:', error);
      return false;
    }
  }, [dispatch]);
  
  const notifyDocumentRejected = useCallback(async (
    userId: string, 
    recipientName?: string,
    notes?: string
  ) => {
    try {
      console.log(`Starting document rejected notification for user: ${userId}`);
      
      // Get the user's email from their ID
      const recipientEmail = await getUserEmail(userId);
      if (!recipientEmail) {
        console.error('Could not find email for user:', userId);
        return false;
      }
      
      console.log(`Found email for user ${userId}: ${recipientEmail}`);
      
      // If recipientName is not provided, fetch it
      if (!recipientName) {
        const { recipientName: fetchedName } = await fetchRecipientProfile(recipientEmail);
        recipientName = fetchedName;
      }
      
      console.log(`Dispatching documentRejected notification to ${recipientEmail}`);
      
      return await dispatch('documentRejected', recipientEmail, {
        recipientName,
        notes
      });
    } catch (error) {
      console.error('Error sending document rejected notification:', error);
      return false;
    }
  }, [dispatch]);
  
  return {
    notifyDocumentVerified,
    notifyDocumentRejected
  };
};

export default useDocumentEmailNotifier;
